/**
 * useServiceHealth Hook
 *
 * Custom hook that subscribes to health documents for all monitored services
 * and returns their current status with real-time updates.
 */

import { useState, useEffect } from 'react';
import { db } from 'config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { getMonitoredServices } from '../config/serviceRegistry';

/**
 * Hook to subscribe to health data for all monitored services
 * @returns {Object} { serviceHealth: { serviceId: healthData }, loading: boolean, error: string|null }
 */
export function useServiceHealth() {
  const [serviceHealth, setServiceHealth] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const monitoredServices = getMonitoredServices();
    const unsubscribes = [];
    let loadedCount = 0;

    // Subscribe to each monitored service's health document
    monitoredServices.forEach((service) => {
      const [collection, docPath, subCollection, subDoc] = service.firestorePath.split('/');
      // Path: systemHealth/services/{serviceName}/current
      const docRef = doc(db, collection, docPath, subCollection, subDoc);

      const unsubscribe = onSnapshot(
        docRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            setServiceHealth((prev) => ({
              ...prev,
              [service.id]: docSnapshot.data()
            }));
          } else {
            // Document doesn't exist yet - set to unknown status
            setServiceHealth((prev) => ({
              ...prev,
              [service.id]: { status: 'unknown', checkedAt: null }
            }));
          }

          loadedCount++;
          if (loadedCount >= monitoredServices.length) {
            setLoading(false);
          }
        },
        (err) => {
          console.error(`Error fetching health for ${service.id}:`, err);
          setError(`Failed to load health data for ${service.name}`);
          setLoading(false);
        }
      );

      unsubscribes.push(unsubscribe);
    });

    // Cleanup all subscriptions on unmount
    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, []);

  return { serviceHealth, loading, error };
}

export default useServiceHealth;
